'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import Image from 'next/image'
import HLSPlayer from './HLSPlayer'

type HeroText = {
  en: React.ReactNode
  ar: React.ReactNode
}

interface HeroContent {
  title: HeroText
  paragraph: HeroText
}

interface HeroSectionProps {
  backgroundimage?: string
  videoSrc?: string
  locale?: string
  alt?: string
  content: HeroContent
  button?: React.ReactNode
  buttonHref?: string
  containerClassName?: string
  paragraphWidthClassName?: string
  headingWidthClassName?: string
}

export const HomeHeroSection: React.FC<HeroSectionProps> = ({
  backgroundimage,
  locale,
  alt,
  content,
  button,
  videoSrc,
  buttonHref = '#',
  containerClassName = 'nav-container',
  paragraphWidthClassName = 'w-full md:w-[80%] lg:w-[70%] xl:w-[60%]',
  headingWidthClassName = 'w-full md:w-[80%] lg:w-[70%] xl:w-[60%]',
}) => {
  const isArabic = locale === 'ar'

  const title = isArabic ? content.title.ar : content.title.en
  const paragraph = isArabic ? content.paragraph.ar : content.paragraph.en

  return (
    <section className="relative w-full min-h-[100svh] lg:h-[700px] xl:h-[800px] 2xl:h-[900px] overflow-hidden">
      {videoSrc ? (
        <div className="absolute inset-0 ">
          <HLSPlayer
            src={videoSrc}
            className="w-full h-full object-cover"
            ariaLabel={alt || 'Hero video background'}
            poster={backgroundimage || ''}
            autoPlay
            muted
            loop
            controls={false}
          />
        </div>
      ) : (
        <Image   
          src={backgroundimage!}
          alt={alt || 'Hero background'}
          fill
          priority
          className="object-cover object-center"
        />
      )}

      {/* Overlays */}
      <div className="absolute inset-0">
        <div className="w-full h-full bg-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent" />
      </div>

      {/* Content aligned to bottom on md+, center on mobile */}
      <div className="relative w-full z-10 flex flex-col justify-end min-h-[100svh] lg:h-[700px] xl:h-[800px] 2xl:h-[900px]">
        <div
          className={clsx(
            containerClassName,
            ` hidden
        mx-auto sm:flex flex-col
        gap-4 md:gap-5 md:px-0
        pt-32 md:pt-0 lg:px-12 lg:pb-20 xl:px-0
        pb-28 md:pb-16 xl:pb-40 2xl:pb-36
         md:text-left
        items-start
        `,
          )}
        >
          <h1
            className={clsx(
              'text-white font-[500] leading-tight ',
              headingWidthClassName,
              isArabic
                ? 'text-[26px] md:text-[30px] xl:text-[50px] text-right'
                : 'text-[32px] md:text-[30px] xl:text-[45px] 2xl:text-[48px]',
            )}
          >
            {title}
          </h1>

          <p
            className={clsx(
              'text-white font-light leading-5 sm:block hidden ',
              paragraphWidthClassName,
              isArabic
                ? 'text-[14px] sm:text-[15px] md:text-[16px] xl:text-[18px] text-right'
                : 'text-[15px] sm:text-[14px] md:text-[14px] xl:text-[16px] 2xl:text-[16px] text-left',
            )}
          >
            {paragraph}
          </p>

          {button && (
            <div className={`mt-3 ${isArabic ? '-mr-[18px] md:mr-[0]' : '-ml-[18px] md:ml-[0]'}`}>
              <Link href={buttonHref} className="inline-block">
                {button}
              </Link>
            </div>
          )}
        </div>
        {/* <div
          className={clsx(
            containerClassName,
            ` sm:hidden
        section-container mx-auto flex flex-col pb-28
        `,
          )}
        > */}

        {/* </div> */}
      </div>
    </section>
  )
}
